import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';

const AvisClients = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 ${
          index < rating
            ? 'fill-yellow-400 text-yellow-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-500">Chargement des avis...</p>
        </div>
      </section>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
            Avis Clients
          </h2>
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="flex">
              {renderStars(5)}
            </div>
            <span className="text-3xl font-bold text-gray-800">{averageRating}</span>
            <span className="text-gray-600">/ 5</span>
          </div>
          <p className="text-gray-600">
            Basé sur {reviews.length} avis vérifiés
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 border border-gray-100"
            >
              {/* Quote Icon */}
              <Quote className="w-10 h-10 text-[#00BCD4] opacity-20 mb-4" />

              {/* Rating */}
              <div className="flex items-center gap-1 mb-3">
                {renderStars(review.rating)}
              </div>

              {/* Review Text */}
              <p className="text-gray-700 mb-4 leading-relaxed">
                {review.text}
              </p>

              {/* Savings Badge */}
              {review.savings && (
                <div className="inline-block bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white text-sm font-semibold px-4 py-2 rounded-full mb-4">
                  💰 {review.savings}
                </div>
              )}

              {/* Author Info */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="font-semibold text-gray-800">{review.name}</p>
                <p className="text-sm text-gray-600">{review.vehicle}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500">📍 {review.location}</p>
                  <p className="text-xs text-gray-500">{review.date}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Google Source Badge */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Avis collectés depuis Google Reviews et témoignages clients directs
          </p>
        </div>
      </div>
    </section>
  );
};

export default AvisClients;
